import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { GoTrash, GoPencil } from "react-icons/go";
import { IoMdAdd } from "react-icons/io";
import { useDarts } from "../../hooks/useDarts";
import { showNotification } from "../../utils/notifications";
import { NOTIFICATION_TYPES } from "../../utils/constants";
import CreateDartPopup from "../CreateDartPopup";

const DartCard = ({ dart, onEdit, onDelete }) => {
  const { t } = useTranslation();

  return (
    <div className="group bg-primary/80 hover:bg-primary rounded-xl shadow-md transition-all duration-200 border border-light/10 hover:border-test/30">
      <div className="p-3">
        {/* Header with name and actions */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-highlight font-bold text-base group-hover:text-test transition-colors truncate max-w-[200px] sm:max-w-none">
                {dart.name}
              </h3>
              {dart.isDefault && (
                <span className="shrink-0 inline-block bg-test/20 text-test text-xs px-2 py-0.5 rounded-full">
                  {t("settings.myDarts.defaultDart")}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <button
              onClick={() => onEdit(dart)}
              className="p-1.5 bg-test/10 hover:bg-test/20 text-test rounded-lg transition-colors"
              title={t("settings.myDarts.editDart")}
            >
              <GoPencil size={14} />
            </button>
            <button
              onClick={() => onDelete(dart.id)}
              className="p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg transition-colors"
              title={t("settings.myDarts.deleteDart")}
            >
              <GoTrash size={14} />
            </button>
          </div>
        </div>

        {/* Specifications */}
        <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 gap-2">
          {dart.weight && (
            <div className="flex items-center gap-2 px-2 py-1 rounded">
              <span className="text-white/60 text-sm shrink-0">
                {t("settings.myDarts.weight")}:
              </span>
              <span className="text-white text-sm truncate">
                {dart.weight}g
              </span>
            </div>
          )}
          {dart.shortCode && (
            <div className="flex items-center gap-2 px-2 py-1 rounded">
              <span className="text-white/60 text-sm shrink-0">
                {t("settings.myDarts.code")}:
              </span>
              <span className="text-white text-sm truncate">
                {dart.shortCode}
              </span>
            </div>
          )}
          {dart.stem && (
            <div className="flex items-center gap-2 px-2 py-1 rounded">
              <span className="text-white/60 text-sm shrink-0">
                {t("settings.myDarts.stem")}:
              </span>
              <span className="text-white text-sm truncate">{dart.stem}</span>
            </div>
          )}
          {dart.flight && (
            <div className="flex items-center gap-2 px-2 py-1 rounded">
              <span className="text-white/60 text-sm shrink-0">
                {t("settings.myDarts.flight")}:
              </span>
              <span className="text-white text-sm truncate">{dart.flight}</span>
            </div>
          )}
        </div>

        {/* Description */}
        {dart.description && (
          <div className="mt-3 pt-2 border-t border-light/10">
            <p className="text-xs text-white/80 break-words">
              {dart.description}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyDartsState = ({ t }) => (
  <div className="text-center py-6 bg-primary/30 rounded-xl">
    <p className="text-white/80 text-sm">{t("settings.myDarts.noDarts")}</p>
    <p className="text-white/60 text-xs mt-1">
      {t("settings.myDarts.getStarted")}
    </p>
  </div>
);

const DartsSection = () => {
  const { t } = useTranslation();
  const { darts, isLoading, saveDart, deleteDart } = useDarts();

  // Popup state
  const [showDartPopup, setShowDartPopup] = useState(false);
  const [editingDart, setEditingDart] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    weight: "",
    description: "",
    shortCode: "",
    stem: "",
    flight: "",
    isDefault: false,
  });

  const resetForm = () => {
    setFormData({
      name: "",
      weight: "",
      description: "",
      shortCode: "",
      stem: "",
      flight: "",
      isDefault: false,
    });
    setEditingDart(null);
  };

  const openNewDartPopup = () => {
    resetForm();
    setShowDartPopup(true);
  };

  const openEditDartPopup = (dart) => {
    setFormData({
      name: dart.name || "",
      weight: dart.weight?.toString() || "",
      description: dart.description || "",
      shortCode: dart.shortCode || "",
      stem: dart.stem || "",
      flight: dart.flight || "",
      isDefault: !!dart.isDefault,
    });
    setEditingDart(dart);
    setShowDartPopup(true);
  };

  const handleSaveDart = async (data) => {
    try {
      const result = await saveDart(data, editingDart?.id);
      showNotification(result.message, NOTIFICATION_TYPES.SUCCESS);
      setShowDartPopup(false);
      resetForm();
    } catch (error) {
      showNotification(error.message, NOTIFICATION_TYPES.ERROR);
    }
  };

  const handleDeleteDart = async (dartId) => {
    try {
      const result = await deleteDart(dartId);
      if (result.success && !result.cancelled) {
        showNotification(result.message, NOTIFICATION_TYPES.SUCCESS);
      }
    } catch (error) {
      showNotification(error.message, NOTIFICATION_TYPES.ERROR);
    }
  };

  const closePopup = () => {
    setShowDartPopup(false);
    resetForm();
  };

  if (isLoading) {
    return (
      <section className="bg-secondary p-6 rounded-2xl shadow-lg">
        <div className="text-center py-8">
          <p className="text-white/80">{t("common.loading")}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-secondary p-4 rounded-2xl shadow-lg">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-semibold text-test">
          {t("settings.myDarts.title")}
        </h2>
        <button
          onClick={openNewDartPopup}
          className="flex items-center gap-2 bg-test hover:bg-test/90 text-primary px-3 py-2 rounded-lg shadow-md transition-all duration-200 w-full sm:w-auto text-sm"
        >
          <IoMdAdd size={18} />
          {t("settings.myDarts.addDart")}
        </button>
      </div>

      {darts.length === 0 ? (
        <EmptyDartsState t={t} />
      ) : (
        <div className="space-y-2">
          {darts.map((dart) => (
            <DartCard
              key={dart.id}
              dart={dart}
              onEdit={openEditDartPopup}
              onDelete={handleDeleteDart}
            />
          ))}
        </div>
      )}

      <CreateDartPopup
        isOpen={showDartPopup}
        isEditing={!!editingDart}
        closePopup={closePopup}
        name={formData.name}
        setName={(name) => setFormData((prev) => ({ ...prev, name }))}
        weight={formData.weight}
        setWeight={(weight) => setFormData((prev) => ({ ...prev, weight }))}
        description={formData.description}
        setDescription={(description) =>
          setFormData((prev) => ({ ...prev, description }))
        }
        shortCode={formData.shortCode}
        setShortCode={(shortCode) =>
          setFormData((prev) => ({ ...prev, shortCode }))
        }
        stem={formData.stem}
        setStem={(stem) => setFormData((prev) => ({ ...prev, stem }))}
        flight={formData.flight}
        setFlight={(flight) => setFormData((prev) => ({ ...prev, flight }))}
        isDefault={formData.isDefault}
        setIsDefault={(isDefault) =>
          setFormData((prev) => ({ ...prev, isDefault }))
        }
        onSave={handleSaveDart}
      />
    </section>
  );
};

export default DartsSection;
